import { ReactNode, Suspense, lazy } from 'react';

import { SidebarPage } from '@backstage/core-components';
import {
  ExtensionDefinition,
  coreExtensionData,
  createExtension,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';

import { useFeatureFlag } from '@spotify/confidence-core-react';

import { SidebarMenu } from './SidebarMenu';

const LazyPortalLayout = lazy(() =>
  import('../Layout/PortalLayout').then(m => ({ default: m.PortalLayout })),
);
const LazyPortalSidebarMenu = lazy(() =>
  import('./PortalSidebarMenu').then(m => ({ default: m.PortalSidebarMenu })),
);

const LayoutContent = ({
  nav,
  content,
}: {
  nav: ReactNode;
  content: ReactNode;
}) => {
  const { enabled: portalNavigationEnabled } = useFeatureFlag<{
    enabled: boolean;
  }>('portal-navigation', { enabled: false });

  if (portalNavigationEnabled) {
    return (
      <Suspense fallback={null}>
        <LazyPortalLayout nav={<LazyPortalSidebarMenu />} content={content} />
      </Suspense>
    );
  }

  return (
    <SidebarPage>
      {nav}
      {content}
    </SidebarPage>
  );
};

export const Layout = createExtension({
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    nav: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  *factory({ inputs }) {
    yield coreExtensionData.reactElement(
      <LayoutContent
        nav={inputs.nav.get(coreExtensionData.reactElement)}
        content={inputs.content.get(coreExtensionData.reactElement)}
      />,
    );
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Backstage ExtensionDefinitionParameters is internal; matching shape requires more imports than a generic widening
}) as ExtensionDefinition<any>;

const SidebarNav = () => {
  const { enabled: portalNavigationEnabled } = useFeatureFlag<{
    enabled: boolean;
  }>('portal-navigation', { enabled: false });

  if (portalNavigationEnabled) {
    return null;
  }
  return <SidebarMenu />;
};

export const Sidebar = createExtension({
  name: 'nav',
  attachTo: { id: 'app/layout', input: 'nav' },
  inputs: {},
  output: [coreExtensionData.reactElement],
  *factory({}) {
    yield coreExtensionData.reactElement(<SidebarNav />);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Backstage ExtensionDefinitionParameters is internal; matching shape requires more imports than a generic widening
}) as ExtensionDefinition<any>;
